import React from 'react';
interface SkeletonProps {
  /** Width class (Tailwind) — default: 'w-full' */
  className?: string;
  /** Variant: 'text' (single line), 'circle', 'rect' (card-sized block) */
  variant?: 'text' | 'circle' | 'rect';
  /** Number of text lines to render (only for variant='text') */
  lines?: number;
}
/**
 * Skeleton loading placeholder — shows animated pulse blocks.
 * Use inside portal pages while data is loading.
 */
export function Skeleton({
  className = '',
  variant = 'text',
  lines = 1
}: SkeletonProps) {
  if (variant === 'circle') {
    return <div className={`rounded-full bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-2649" data-mp="1d7v7v0rxeh84" />;
  }
  if (variant === 'rect') {
    return <div className={`rounded-2xl bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-2650" data-mp="1m5xf3y29663u" />;
  }
  // Text variant — render multiple lines
  return <div className={`space-y-2.5 ${className}`} aria-hidden="true" data-id="element-2651" data-mp="3sx55uc952de3">
      {Array.from({
      length: lines
    }).map((_, i) => <div key={i} className={`h-4 bg-slate-200 rounded-md animate-pulse ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`} data-id="element-2652" data-mp="1t7wctae8q2eg" data-mpi={i} />)}
    </div>;
}
/**
 * Skeleton card — mimics a StatCard or summary card while loading.
 */
export function SkeletonCard({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`bg-white border border-slate-200 rounded-2xl p-6 shadow-sm ${className}`} aria-hidden="true" data-id="element-2653" data-mp="27wsix1wql5cf">
      
      <div className="flex items-center gap-4" data-id="element-2654" data-mp="2bz5n3lvu4v73">
        <Skeleton variant="circle" className="w-12 h-12" data-id="element-2655" data-mp="2c0gqhkldc0xo" />
        <div className="flex-1 space-y-2" data-id="element-2656" data-mp="2gwfl3pxaif6j">
          <Skeleton className="w-24" data-id="element-2657" data-mp="2gzjv53xfs554" />
          <Skeleton className="w-16 h-6" data-id="element-2658" data-mp="2lw0me6zxmzmf" />
        </div>
      </div>
    </div>;
}
/**
 * Skeleton page — full page loading state for portal pages.
 * Shows 3 summary cards + a content block.
 */
export function SkeletonPage() {
  return <div className="max-w-7xl mx-auto p-6 space-y-8" aria-busy="true" aria-label="Laden..." data-id="element-2659" data-mp="rju6ww009wob">
      
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2660" data-mp="wjq3wbaacnud">
        <SkeletonCard data-id="element-2661" data-mp="ohpfk0sm0m6u" />
        <SkeletonCard data-id="element-2662" data-mp="oh67rcwobe6j" />
        <SkeletonCard data-id="element-2663" data-mp="jhuonx8u3wls" />
      </div>

      {/* Content block */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-id="element-2664" data-mp="jje7nvlvklr9">
        <Skeleton className="w-48 h-5" data-id="element-2665" data-mp="ygoqxr4revxj" />
        <div className="space-y-3" data-id="element-2666" data-mp="tg0om9b2yso0">
          {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-4" data-id="element-2667" data-mp="tiddl6f9a3ak" data-mpi={i}>
              <Skeleton variant="circle" className="w-10 h-10" data-id="element-2668" data-mp="17e7ep39sfod4" />
              <div className="flex-1" data-id="element-2669" data-mp="12h8kkkq4jhex">
                <Skeleton className="w-32" data-id="element-2670" data-mp="12gfcngffilv7" />
              </div>
              <Skeleton variant="rect" className="w-24 h-3" data-id="element-2671" data-mp="1h5v30vb1spad" />
              <Skeleton className="w-10 h-5" data-id="element-2672" data-mp="1h7fz4pd0ggac" />
            </div>)}
        </div>
      </div>
    </div>;
}